"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import type { FinancialConnection } from "@features/api/memberApi";
import { useMemoizedFn } from "ahooks";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import {
  fetchPortfolioConnectionsQueryData,
  PORTFOLIO_CONNECTIONS_QUERY_KEY,
} from "./portfolioConnectionsQuery";

export { PORTFOLIO_CONNECTIONS_QUERY_KEY } from "./portfolioConnectionsQuery";

export interface UsePortfolioConnectionsResult {
  /** All active manual connections (not filtered by account type). */
  manualConnections: FinancialConnection[];

  /** Active connections with at least one INVESTMENT sub-account (plaid + manual merged). */
  investConnections: FinancialConnection[];

  /** Active connections with at least one non-INVESTMENT sub-account (plaid + manual merged). */
  nonInvestConnections: FinancialConnection[];

  /** Backward compat: active + INVESTMENT plaid connections. */
  plaidConnections: FinancialConnection[];
  /** Backward compat: active + INVESTMENT manual connections. */
  plaidManualConnections: FinancialConnection[];

  isLoading: boolean;
  isFetching: boolean;
  /** First fetch finished and no in-flight refetch — safe for mode / Connect-card branching. */
  isConnectionsReady: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  invalidate: () => Promise<void>;
}

export const usePortfolioConnections = (): UsePortfolioConnectionsResult => {
  const { memberApi } = useOspApiClientProvider();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
    enabled: Boolean(memberApi),
    queryFn: async () => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }
      return fetchPortfolioConnectionsQueryData(memberApi);
    },
  });

  const invalidate = useMemoizedFn(async () => {
    await queryClient.invalidateQueries({
      queryKey: PORTFOLIO_CONNECTIONS_QUERY_KEY,
    });
  });

  return {
    manualConnections: query.data?.manualConnections ?? [],
    investConnections: query.data?.investConnections ?? [],
    nonInvestConnections: query.data?.nonInvestConnections ?? [],
    plaidConnections: query.data?.plaidConnections ?? [],
    plaidManualConnections: query.data?.plaidManualConnections ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isConnectionsReady:
      Boolean(memberApi) && query.isFetched && query.fetchStatus === "idle",
    error: query.error instanceof Error ? query.error : null,
    refetch: query.refetch,
    invalidate,
  };
};
